import { TransactionListDto } from '../../../../tenant/transactions/dtos/transaction-list.dto';
import { EXCLUDED_TRANSACTION_STATUSES } from '../consts/excluded-transaction-statuses.const';
import { RentalAgreementAccountStatementInterface } from '../interfaces/rental-agreement-account-statement.interface';
import { RentalAgreementAccountStatementTransactionInterface } from '../interfaces/rental-agreement-account-statement-transaction.interface';
import { toSignedPaymentAmount } from './to-signed-payment-amount.function';

/**
 * Maps the charges total and transaction data into a print-only account statement.
 * One sign convention for agreements and reservations: receipts and deposits
 * positive, refunds negative.
 */
export function toRentalAccountStatement(
  transactions: TransactionListDto[],
  totalChargedGross: number,
): RentalAgreementAccountStatementInterface {
  const paymentTransactions: RentalAgreementAccountStatementTransactionInterface[] =
    [...(transactions ?? [])]
      .filter(
        (transaction) =>
          !EXCLUDED_TRANSACTION_STATUSES.includes(transaction.status),
      )
      .sort((leftTransaction, rightTransaction) => {
        const leftCreatedAt = new Date(
          leftTransaction.createdAt ?? 0,
        ).getTime();
        const rightCreatedAt = new Date(
          rightTransaction.createdAt ?? 0,
        ).getTime();

        if (leftCreatedAt !== rightCreatedAt) {
          return leftCreatedAt - rightCreatedAt;
        }

        return (leftTransaction.id ?? 0) - (rightTransaction.id ?? 0);
      })
      .map((transaction) => ({
        id: transaction.id,
        createdAt: transaction.createdAt ?? new Date(0),
        code: transaction.code,
        externalReference: transaction.externalReference,
        description: transaction.description,
        type: transaction.type,
        status: transaction.status,
        amount: toSignedPaymentAmount(transaction),
      }));

  const totalCharged = Number(totalChargedGross ?? 0);
  const totalPaid = paymentTransactions.reduce(
    (runningTotal, transaction) => runningTotal + transaction.amount,
    0,
  );

  return {
    summary: {
      totalCharged,
      totalPaid,
      balanceDue: totalCharged - totalPaid,
    },
    paymentTransactions,
  };
}
